import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { planningChat, getChatHistory, createNode, updateNode, deleteNode, createResource, createHabit, createPlanningTodo } from '../../actions/planningAction';
import './PlanningChat.css';

const TYPE_COLOR = {
  milestone: '#f17501',
  action:    '#34d399',
  obstacle:  '#fb7185',
  doubt:     '#fbbf24',
  assumption:'#60a5fa',
  belief:    '#f17501',
  decision:  '#fb923c',
  note:      '#94a3b8',
};

// ── Accept-All Modal ──────────────────────────────────────────────────────────

function AcceptAllModal({ count, progress, onConfirm, onCancel }) {
  if (progress) {
    return (
      <div className="pc-bulk-toast">
        <div className="pc-bulk-spinner" />
        <div className="pc-bulk-text">
          <span className="pc-bulk-label">Creating nodes</span>
          <span className="pc-bulk-count">{progress.done} / {progress.total}</span>
        </div>
      </div>
    );
  }
  return (
    <div className="pc-modal-overlay" onClick={onCancel}>
      <div className="pc-modal" onClick={e => e.stopPropagation()}>
        <div className="pc-modal-icon">⚡</div>
        <h3 className="pc-modal-title">Apply all {count} suggestions?</h3>
        <p className="pc-modal-sub">Nodes will be created in order. Review them in the planner after.</p>
        <div className="pc-modal-btns">
          <button className="pc-modal-confirm" onClick={onConfirm}>Apply all {count}</button>
          <button className="pc-modal-cancel" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Confirm Modal ─────────────────────────────────────────────────────────────

function ConfirmModal({ pending, onConfirm, onCancel, confirming }) {
  if (!pending) return null;
  const { sug } = pending;
  const action = sug.action || 'create';
  const isDelete = action === 'delete';
  const isEdit   = action === 'edit';
  const isResource = action === 'create_resource';

  return (
    <div className="pc-modal-overlay" onClick={onCancel}>
      <div className="pc-modal" onClick={e => e.stopPropagation()}>
        <div className={`pc-modal-icon ${isDelete ? 'pc-modal-icon--danger' : ''}`}>
          {isDelete ? '⚠' : isEdit ? '✎' : isResource ? '◆' : '+'}
        </div>

        <h3 className="pc-modal-title">
          {isDelete ? 'Delete this node?' : isEdit ? 'Apply these changes?' : isResource ? 'Create this document?' : 'Create this node?'}
        </h3>

        {isResource && (
          <div className="pc-modal-body">
            <p className="pc-modal-detail"><strong>{sug.title}</strong></p>
            {sug.reason && <p className="pc-modal-sub">{sug.reason}</p>}
            {sug.parentLabel && <p className="pc-modal-sub">Linked to: {sug.parentLabel}</p>}
          </div>
        )}

        {action === 'create' && (
          <div className="pc-modal-body">
            <span className="pc-modal-type-pill" style={{ '--sug-color': TYPE_COLOR[sug.type] || '#f17501' }}>
              {sug.type}
            </span>
            <p className="pc-modal-detail">{sug.content}</p>
            {sug.parentLabel && (
              <p className="pc-modal-sub">Under: {sug.parentLabel}</p>
            )}
          </div>
        )}

        {isEdit && (
          <div className="pc-modal-body">
            <p className="pc-modal-sub">Node: <strong>{sug.nodeLabel}</strong></p>
            {sug.changes?.content && (
              <p className="pc-modal-detail">"{sug.changes.content}"</p>
            )}
            {sug.changes?.status && (
              <p className="pc-modal-sub">Status → <strong>{sug.changes.status}</strong></p>
            )}
          </div>
        )}

        {isDelete && (
          <div className="pc-modal-body">
            <p className="pc-modal-detail pc-modal-detail--danger">"{sug.nodeLabel}"</p>
            {sug.reason && <p className="pc-modal-sub">{sug.reason}</p>}
            <p className="pc-modal-warn">This will also delete all child nodes.</p>
          </div>
        )}

        <div className="pc-modal-btns">
          <button
            className={`pc-modal-confirm${isDelete ? ' pc-modal-confirm--danger' : ''}`}
            onClick={onConfirm}
            disabled={confirming}
          >
            {confirming ? '…' : isDelete ? 'Delete' : isEdit ? 'Apply' : 'Create'}
          </button>
          <button className="pc-modal-cancel" onClick={onCancel} disabled={confirming}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Suggestion Card ───────────────────────────────────────────────────────────

function SuggestionCard({ sug, status, onRequest, onDecline, onCardClick }) {
  const action = sug.action || 'create';
  const isDelete = action === 'delete';
  const isEdit   = action === 'edit';
  const isResource = action === 'create_resource';
  const isHabit  = action === 'create_habit';
  const isTodo   = action === 'create_todo';
  const color = isDelete ? '#fb7185' : isEdit ? '#fbbf24' : isResource ? '#f17501' : isHabit ? '#22d3ee' : isTodo ? '#a78bfa' : (TYPE_COLOR[sug.type] || '#f17501');
  const targetNodeId = (isDelete || isEdit) ? sug.nodeId : sug.parentId;

  const scheduleText = isHabit
    ? (sug.scheduleType === 'weekdays'
        ? 'Specific days'
        : sug.scheduleType === 'weekly'
          ? `${sug.timesPerWeek || 3}×/week`
          : 'Daily')
    : '';

  if (status === 'declined') return null;

  if (status === 'accepted') {
    return (
      <div className="pc-sug-card pc-sug-card--accepted">
        <span className="pc-sug-check">✓</span>
        <span className="pc-sug-accepted-text">
          {isDelete ? `Deleted: ${sug.nodeLabel}`
            : isEdit ? `Updated: ${sug.nodeLabel}`
            : isResource ? `Document created: ${sug.title}`
            : isHabit ? `Habit added: ${sug.title || sug.content}`
            : isTodo ? `Todo added: ${sug.text || sug.content}`
            : (sug.content || '').slice(0, 55)}
          {!isDelete && !isEdit && !isResource && !isHabit && !isTodo && (sug.content?.length > 55 ? '…' : '')}
        </span>
      </div>
    );
  }

  const failed = status === 'failed';

  return (
    <div
      className="pc-sug-card pc-sug-card--clickable"
      style={{ '--sug-color': color }}
      onClick={() => targetNodeId && onCardClick?.(targetNodeId)}
      title={targetNodeId ? 'Click to open node in planner' : undefined}
    >
      <div className="pc-sug-header">
        {action === 'create' && (
          <span className="pc-sug-type">
            {sug.type === 'node' && sug.tag ? sug.tag : sug.type}
          </span>
        )}
        {isResource && <span className="pc-sug-action-badge pc-sug-action-badge--resource">document</span>}
        {isHabit  && <span className="pc-sug-action-badge pc-sug-action-badge--habit">habit · {scheduleText}</span>}
        {isTodo   && <span className="pc-sug-action-badge pc-sug-action-badge--todo">todo{sug.due ? ` · due ${sug.due}` : ''}</span>}
        {isEdit   && <span className="pc-sug-action-badge pc-sug-action-badge--edit">edit</span>}
        {isDelete && <span className="pc-sug-action-badge pc-sug-action-badge--delete">delete</span>}
        {(sug.parentLabel || sug.nodeLabel) && (
          <span className="pc-sug-parent">
            {isDelete || isEdit ? sug.nodeLabel : `↳ ${sug.parentLabel}`}
          </span>
        )}
        {targetNodeId && <span className="pc-sug-open-hint">↗</span>}
      </div>

      {isResource && (
        <>
          <p className="pc-sug-content"><strong>{sug.title}</strong></p>
          {sug.reason && <p className="pc-sug-content pc-sug-content--muted">{sug.reason}</p>}
        </>
      )}
      {action === 'create' && <p className="pc-sug-content">{sug.content}</p>}
      {isHabit && (
        <>
          <p className="pc-sug-content"><strong>{sug.title || sug.content}</strong></p>
          {sug.reason && <p className="pc-sug-content pc-sug-content--muted">{sug.reason}</p>}
        </>
      )}
      {isTodo && <p className="pc-sug-content">{sug.text || sug.content}</p>}
      {isEdit && sug.changes?.content && (
        <p className="pc-sug-content">"{sug.changes.content}"</p>
      )}
      {isEdit && sug.changes?.status && !sug.changes?.content && (
        <p className="pc-sug-content">Status → {sug.changes.status}</p>
      )}
      {isDelete && sug.reason && (
        <p className="pc-sug-content pc-sug-content--muted">{sug.reason}</p>
      )}

      {failed && (
        <p className="pc-sug-content pc-sug-failed-note">⚠ Couldn't apply. Retry?</p>
      )}

      <div className="pc-sug-btns" onClick={e => e.stopPropagation()}>
        <button
          className={`pc-sug-accept${isDelete ? ' pc-sug-accept--danger' : ''}`}
          style={!isDelete ? { '--sug-color': color } : undefined}
          onClick={onRequest}
        >
          {failed ? '↻ Retry' : isDelete ? '🗑 Delete' : isEdit ? '✎ Apply' : isResource ? '◆ Create document' : isHabit ? '↻ Create habit' : isTodo ? '✓ Add todo' : '+ Add node'}
        </button>
        <button className="pc-sug-decline" onClick={onDecline}>Skip</button>
      </div>
    </div>
  );
}

// ── Node Picker Dropup ────────────────────────────────────────────────────────

function NodePickerDropup({ milestones, onSelect, onClose }) {
  const [q, setQ] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const flat = (milestones || []).flatMap(m => [
    m,
    ...(m.actions || []).flatMap(a => [a, ...(a.subNodes || [])]),
  ]);
  const filtered = q
    ? flat.filter(n => n.content?.toLowerCase().includes(q.toLowerCase()))
    : flat;

  return (
    <div className="pc-picker-dropup" ref={ref}>
      <input
        className="pc-picker-search"
        autoFocus
        placeholder="Search nodes…"
        value={q}
        onChange={e => setQ(e.target.value)}
      />
      <div className="pc-picker-list">
        {filtered.length === 0 && <p className="pc-picker-empty">No nodes found</p>}
        {filtered.map(n => (
          <button
            key={n._id}
            className="pc-picker-item"
            onClick={() => onSelect(n)}
          >
            <span
              className="pc-picker-dot"
              style={{ background: TYPE_COLOR[n.type] || '#f17501' }}
            />
            <span className="pc-picker-node-content">{n.content}</span>
            <span className="pc-picker-node-type">{n.type}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

const WELCOME_EMPTY = "This vision has no nodes yet. Tell me what you're trying to build and what success looks like for you. I'll help you break it into milestones and actions.";
const WELCOME_EXISTS = "I have full context on this vision. Ask me anything — what to focus on, what you might be missing, how to think through an obstacle.";
const WELCOME_DOC = "I can see this document and what it's linked to. Tell me what you want in it, or ask me to draft it — I'll write it right here when you're ready.";

export default function PlanningChat({ visionId, milestones, onRefresh, onFocusNode, onAfterApply, initialAttachedNode, theme, resourceId, onDocUpdate }) {
  const dispatch = useDispatch();
  const { chatLoading } = useSelector(s => s.planning);

  const sessionKey = `dmyor_chat_session_${visionId}`;
  const sessionId = useRef(
    localStorage.getItem(sessionKey) || (() => {
      const id = `s_${Date.now()}`;
      localStorage.setItem(sessionKey, id);
      return id;
    })()
  );

  const [messages,      setMessages]      = useState(null);
  const [input,         setInput]         = useState('');
  const [attachedNode,  setAttachedNode]  = useState(null);
  const [showPicker,    setShowPicker]    = useState(false);

  // Sync when a node is attached from outside (e.g. Agent button on a card)
  useEffect(() => {
    if (initialAttachedNode) setAttachedNode(initialAttachedNode);
  }, [initialAttachedNode?.ts]); // ts changes each time a new node is pushed // eslint-disable-line react-hooks/exhaustive-deps
  const [pending,       setPending]       = useState(null); // { msgIdx, sugIdx, sug }
  const [confirming,    setConfirming]    = useState(false);
  const [pendingAll,    setPendingAll]    = useState(null); // { msgIdx, sugs: [{si, sug}] }
  const [allProgress,   setAllProgress]  = useState(null); // { done, total }
  const bottomRef = useRef(null);
  // Per-message map of suggestion index → created node id, so "new:N" parent
  // refs resolve even when suggestions are applied one at a time.
  const idMapsRef = useRef({});

  const welcomeText = () => resourceId
    ? WELCOME_DOC
    : (!milestones || milestones.length === 0) ? WELCOME_EMPTY : WELCOME_EXISTS;

  useEffect(() => {
    dispatch(getChatHistory(visionId, sessionId.current))
      .then(({ messages: history }) => {
        if (!history.length) {
          setMessages([{ role: 'assistant', content: welcomeText() }]);
          return;
        }
        setMessages(history.map(m => ({
          role: m.role,
          content: m.content,
          suggestions: m.suggestions || null,
          sugStatus: {},
          historical: true,
        })));
      })
      .catch(() => setMessages([{ role: 'assistant', content: welcomeText() }]));
  }, [visionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (messages) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || chatLoading) return;
    const node = attachedNode;
    setInput('');
    setAttachedNode(null);
    setShowPicker(false);
    setMessages(prev => [...prev, { role: 'user', content: text, attachedNode: node || null }]);
    try {
      const payload = { message: text, sessionId: sessionId.current };
      if (node) payload.nodeId = node._id;
      if (resourceId) payload.resourceId = resourceId;
      const res = await dispatch(planningChat(visionId, payload));
      const wrote = !!res.docBody;
      if (wrote) onDocUpdate?.({ title: res.docTitle, body: res.docBody });
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: res.reply,
        suggestions: res.suggestions?.length ? res.suggestions : null,
        sugStatus: {},
        wrote,
      }]);
    } catch (err) {
      if (err?.response?.status === 402) {
        const msg = err?.response?.data?.message
          || 'You have used all your free AI requests. Subscribe to keep using Dmyor Assist.';
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `${msg} [Unlock unlimited AI](/dashboard/upgrade)`,
        }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: 'Something went wrong. Try again.' }]);
      }
    }
  };

  const requestAction = (msgIdx, sugIdx, sug) => {
    setPending({ msgIdx, sugIdx, sug });
  };

  // Flat list of all existing nodes, for id/label fallback matching
  const flatExisting = useMemo(() => {
    const out = [];
    for (const m of (milestones || [])) {
      out.push(m);
      for (const a of (m.actions || [])) {
        out.push(a);
        for (const s of (a.subNodes || [])) out.push(s);
      }
      for (const s of (m.subNodes || [])) out.push(s);
    }
    return out;
  }, [milestones]);

  const findByLabel = (label) => {
    if (!label) return undefined;
    const q = label.trim().toLowerCase();
    if (!q) return undefined;
    const exact = flatExisting.find(n => (n.content || '').trim().toLowerCase() === q);
    if (exact) return String(exact._id);
    const partial = flatExisting.find(n => (n.content || '').trim().toLowerCase().includes(q));
    return partial ? String(partial._id) : undefined;
  };

  const resolveParentId = (parentId, idMap, parentLabel) => {
    // 1. new:N placeholder → look up id created earlier in this batch
    if (typeof parentId === 'string' && parentId.startsWith('new:')) {
      const idx = parseInt(parentId.slice(4), 10);
      if (idMap[idx]) return idMap[idx];
      // Index unresolved (LLM miscount or applied across reload) — fall back to label
      const byLabel = findByLabel(parentLabel);
      if (byLabel) return byLabel;
      throw new Error(`Parent suggestion #${idx} not created yet (no label fallback)`);
    }
    // 2. Explicit id that matches an existing node → use it
    if (parentId && flatExisting.some(n => String(n._id) === String(parentId))) {
      return parentId;
    }
    // 3. No usable parentId but a label is given → try to match existing node
    const byLabel = findByLabel(parentLabel);
    if (byLabel) return byLabel;
    // 4. Truly top-level (milestone) or unresolvable
    if (!parentId) return undefined;
    // Id given but not found locally — pass through, backend will validate
    return parentId;
  };

  const getIdMap = (msgIdx) => {
    if (!idMapsRef.current[msgIdx]) idMapsRef.current[msgIdx] = {};
    return idMapsRef.current[msgIdx];
  };

  const executeOne = async (sug, idMap) => {
    const action = sug.action || 'create';
    if (action === 'create_resource') {
      const parentId = resolveParentId(sug.parentId, idMap, sug.parentLabel);
      await dispatch(createResource(visionId, {
        title: sug.title || 'Untitled',
        body: '',
        linkedNodeIds: parentId ? [parentId] : [],
      }));
      return null;
    }
    if (action === 'create_habit') {
      const linkedNodeId = resolveParentId(sug.parentId, idMap, sug.parentLabel);
      if (!linkedNodeId) throw new Error('A habit must be linked to a node');
      const schedule = ['daily', 'weekdays', 'weekly'].includes(sug.scheduleType) ? sug.scheduleType : 'daily';
      await dispatch(createHabit(visionId, {
        title: sug.title || sug.content || 'Habit',
        scheduleType: schedule,
        weekdays: Array.isArray(sug.weekdays) ? sug.weekdays : [],
        timesPerWeek: sug.timesPerWeek || 3,
        linkedNodeId,
      }));
      return null;
    }
    if (action === 'create_todo') {
      // A todo may be unlinked; resolveParentId returns undefined when there's no node.
      let nodeId;
      try { nodeId = resolveParentId(sug.parentId, idMap, sug.parentLabel); } catch (_) { nodeId = undefined; }
      const nodeContent = nodeId
        ? (flatExisting.find(n => String(n._id) === String(nodeId))?.content || sug.parentLabel || null)
        : null;
      await dispatch(createPlanningTodo(visionId, {
        text: (sug.text || sug.content || '').trim(),
        due: sug.due || null,
        nodeId: nodeId || null,
        nodeContent: nodeContent ? String(nodeContent).slice(0, 120) : null,
      }));
      return null;
    }
    if (action === 'create') {
      const parentId = resolveParentId(sug.parentId, idMap, sug.parentLabel);
      console.log('[chat:create]', {
        type: sug.type, rawParent: sug.parentId, resolvedParent: parentId,
        content: (sug.content || '').slice(0, 40), idMap: { ...idMap },
      });
      const res = await dispatch(createNode(visionId, {
        type:       sug.type,
        content:    sug.content,
        customType: sug.tag || '',
        parentId,
      }));
      console.log('[chat:created]', res?.node?._id, '→ parent', res?.node?.parentId);
      return res?.node?._id;
    } else if (action === 'edit') {
      await dispatch(updateNode(sug.nodeId, sug.changes));
    } else if (action === 'delete') {
      await dispatch(deleteNode(sug.nodeId));
    }
  };

  const setSugStatus = (msgIdx, sugIdx, status) => {
    setMessages(prev => prev.map((m, i) => i !== msgIdx ? m : {
      ...m, sugStatus: { ...m.sugStatus, [sugIdx]: status },
    }));
  };

  const confirmAction = async () => {
    if (!pending) return;
    const { msgIdx, sugIdx, sug } = pending;
    const idMap = getIdMap(msgIdx);
    setConfirming(true);
    try {
      const newId = await executeOne(sug, idMap);
      if (newId) idMap[sugIdx] = newId;
      await onRefresh?.();
      if (newId) onFocusNode?.(newId);
      setSugStatus(msgIdx, sugIdx, 'accepted');
      onAfterApply?.();
    } catch (err) {
      console.error('[chat:confirmAction failed]', err?.response?.data?.message || err?.message, sug);
      setSugStatus(msgIdx, sugIdx, 'failed');
    }
    setConfirming(false);
    setPending(null);
  };

  const confirmAll = async () => {
    if (!pendingAll) return;
    const { msgIdx, sugs } = pendingAll;
    const total = sugs.length;
    const idMap = getIdMap(msgIdx);
    setAllProgress({ done: 0, total });
    let lastId = null;
    for (let i = 0; i < sugs.length; i++) {
      const { si, sug } = sugs[i];
      try {
        const newId = await executeOne(sug, idMap);
        if (newId) {
          idMap[si] = newId;
          lastId = newId;
          // Refresh so the next suggestion's parent lookup sees the new node,
          // but do NOT focus mid-loop — focusing re-drills the dashboard and
          // unmounts cards before their connectors can be measured.
          await onRefresh?.();
        }
        setSugStatus(msgIdx, si, 'accepted');
      } catch (err) {
        console.error('[chat:confirmAll failed]', si, err?.response?.data?.message || err?.message, sug);
        setSugStatus(msgIdx, si, 'failed');
      }
      setAllProgress({ done: i + 1, total });
    }
    // Single final refresh + focus once everything has settled
    await onRefresh?.();
    setAllProgress(null);
    setPendingAll(null);
    if (lastId) onFocusNode?.(lastId);
    onAfterApply?.();
  };

  const declineSug = (msgIdx, sugIdx) => {
    setMessages(prev => prev.map((m, i) => i !== msgIdx ? m : {
      ...m, sugStatus: { ...m.sugStatus, [sugIdx]: 'declined' },
    }));
  };

  const handleKey = e => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const wrapCls = `pc-wrap${theme === 'light' ? ' pc-wrap--light' : ''}`;

  if (messages === null) {
    return (
      <div className={wrapCls}>
        <div className="pc-header">
          <span className="pc-title">Chat</span>
          <span className="pc-sub">AI has full vision context</span>
        </div>
        <div className="pc-loading">Loading history…</div>
      </div>
    );
  }

  return (
    <div className={wrapCls}>
      <ConfirmModal
        pending={pending}
        onConfirm={confirmAction}
        onCancel={() => !confirming && setPending(null)}
        confirming={confirming}
      />
      {pendingAll && (
        <AcceptAllModal
          count={pendingAll.sugs.length}
          progress={allProgress}
          onConfirm={confirmAll}
          onCancel={() => !allProgress && setPendingAll(null)}
        />
      )}

      <div className="pc-header">
        <span className="pc-title">Chat</span>
        <span className="pc-sub">AI has full vision context</span>
      </div>

      <div className="pc-messages">
        {messages.map((m, msgIdx) => (
          <div key={msgIdx} className={`pc-msg pc-msg--${m.role}`}>
            <div className="pc-bubble">
              {m.role === 'assistant' ? (
                <>
                  <div className="pc-md">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {m.content}
                    </ReactMarkdown>
                  </div>
                  {m.wrote && <span className="pc-msg-wrote">✓ Updated the document</span>}
                  {m.suggestions && !m.historical && (() => {
                    const activeSugs = m.suggestions
                      .map((sug, si) => ({ si, sug }))
                      .filter(({ si }) => !m.sugStatus?.[si] || m.sugStatus[si] === 'failed');
                    return (
                      <div className="pc-suggestions">
                        <div className="pc-sug-header-row">
                          <span className="pc-sug-label">Suggested actions</span>
                          {activeSugs.length >= 2 && (
                            <button
                              className="pc-sug-accept-all"
                              onClick={() => setPendingAll({ msgIdx, sugs: activeSugs })}
                            >
                              ⚡ Apply all {activeSugs.length}
                            </button>
                          )}
                        </div>
                        {m.suggestions.map((sug, si) => (
                          <SuggestionCard
                            key={si}
                            sug={sug}
                            status={m.sugStatus?.[si]}
                            onRequest={() => requestAction(msgIdx, si, sug)}
                            onDecline={() => declineSug(msgIdx, si)}
                            onCardClick={onFocusNode}
                          />
                        ))}
                      </div>
                    );
                  })()}
                </>
              ) : (
                <>
                  {m.attachedNode && m.attachedNode.content && (
                    <div className="pc-msg-node-chip">
                      <span
                        className="pc-msg-node-dot"
                        style={{ background: TYPE_COLOR[m.attachedNode.type] || '#f17501' }}
                      />
                      <span className="pc-msg-node-label">
                        {m.attachedNode.content.length > 45
                          ? m.attachedNode.content.slice(0, 45) + '…'
                          : m.attachedNode.content}
                      </span>
                    </div>
                  )}
                  {m.content}
                </>
              )}
            </div>
          </div>
        ))}
        {chatLoading && (
          <div className="pc-msg pc-msg--assistant">
            <div className="pc-bubble pc-bubble--typing"><span /><span /><span /></div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="pc-input-wrap">
        {showPicker && (
          <NodePickerDropup
            milestones={milestones}
            onSelect={n => { setAttachedNode(n); setShowPicker(false); }}
            onClose={() => setShowPicker(false)}
          />
        )}
        {attachedNode && attachedNode.content && (
          <div className="pc-attached-chip">
            <span
              className="pc-attached-dot"
              style={{ background: TYPE_COLOR[attachedNode.type] || '#f17501' }}
            />
            <span className="pc-attached-label">
              {attachedNode.content.length > 48
                ? attachedNode.content.slice(0, 48) + '…'
                : attachedNode.content}
            </span>
            <button
              className="pc-attached-remove"
              onClick={() => setAttachedNode(null)}
              title="Remove"
            >×</button>
          </div>
        )}
        <div className="pc-input-row">
          <button
            className={`pc-attach${showPicker ? ' pc-attach--active' : ''}`}
            onClick={() => setShowPicker(p => !p)}
            title="Attach a node"
            disabled={chatLoading}
          >+</button>
          <textarea
            className="pc-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Ask anything..."
            rows={2}
            disabled={chatLoading}
          />
          <button className="pc-send" onClick={send} disabled={!input.trim() || chatLoading}>↑</button>
        </div>
      </div>
    </div>
  );
}
